<script setup lang="ts">
    import logo from '~/assets/img/logo.png';

    import { useAuthStore } from '@whaleden/core/store/auth';

    const { $keycloak } = useNuxtApp();
    const authStore = useAuthStore();

    const trimmedUsername = computed(() => {
        if (authStore.user && authStore.user.preferred_username) {
            return authStore.user.preferred_username.split('@')[0];
        }
        return '';
    });

    const { locale } = useI18n();

    defineProps<{
        fixed?: boolean;
    }>();
    const emit = defineEmits(['close']);

    const signup = () => {
        const redirectUri =
            locale.value && locale.value !== 'en'
                ? `${window.location.origin}/${locale.value}/account'`
                : `${window.location.origin}/account`;
        $keycloak.register({ redirectUri });
    };

    const signin = () => {
        const redirectUri =
            locale.value && locale.value !== 'en'
                ? `${window.location.origin}/${locale.value}/account`
                : `${window.location.origin}/account`;
        $keycloak.login({ redirectUri, locale: locale.value });
    };

    const logout = async () => {
        const redirectUri =
            locale.value && locale.value !== 'en'
                ? `${window.location.origin}/${locale.value}`
                : window.location.origin;
        $keycloak.logout({ redirectUri });
    };

    const collectUrl = computed(() => {
        return locale.value && locale.value !== 'en'
            ? `https://collect.whaleden.com/${locale.value}`
            : 'https://collect.whaleden.com';
    });

    const shopUrl = computed(() => {
        return locale.value && locale.value !== 'en'
            ? `https://shop.whaleden.com/${locale.value}`
            : 'https://shop.whaleden.com';
    });
</script>

<template>
    <div class="block lg:z-[1000] mt-4 lg:mt-0 !relative">
        <div
            :class="fixed ? 'lg:fixed' : ' lg:absolute'"
            class="tracking-tight hidden bg-white lg:flex left-0 w-full px-16 md:px-8 flex-col lg:flex-row text-black flex gap-8 md:gap-12 top-0 right-0"
        >
            <NuxtLinkLocale class="cursor-pointer" to="/" custom v-slot="{ navigate, href }">
                <div class="flex space-x-2 mt-2.5 mb-2.5 items-center" :href="href" @click="navigate">
                    <img :src="logo" class="h-7" />
                    <p class="text-[1.35rem] select-none font-logo text-black/90 font-semibold tracking-tight">
                        Whaleden
                    </p>
                </div>
            </NuxtLinkLocale>

            <a
                target="_blank"
                :href="collectUrl"
                class="font-medium border-b-4 pb-4 pt-5 cursor-pointer hover:!border-primary"
            >
                {{ $t('onboard.bonus.sections.whaleden.cta') }}
            </a>

            <a
                target="_blank"
                :href="shopUrl"
                class="font-medium border-b-4 pb-4 pt-5 cursor-pointer hover:!border-primary"
            >
                {{ $t('navbar.shop') }}
            </a>

            <Button
                @click="
                    () => {
                        $emit('close');
                        $router.push(locale && locale !== 'en' ? `/${locale}/blog` : '/blog');
                    }
                "
                rel="noopener noreferrer"
                class="font-medium border-b-4 pb-4 pt-5 w-fit border-opacity-10 cursor-pointer hover:border-opacity-100 border-primary"
            >
                {{ $t('navbar.blog') }}
            </Button>
        </div>

        <div class="lg:hidden">
            <div
                class="lg:absolute z-20 flex space-x-2 lg:right-4 lg:bottom-auto lg:left-auto lg:top-4"
                v-if="!$keycloak?.authenticated"
            >
                <div class="shadow-2xl shadow-black/80 rounded-md">
                    <AppButton @click="signin">{{ $t('auth.signIn') }}</AppButton>
                </div>

                <div class="shadow-2xl shadow-black/80 rounded-md">
                    <AppButton isSecondary @click="signup">{{ $t('auth.signUp') }}</AppButton>
                </div>
            </div>
            <div
                v-if="$keycloak?.authenticated"
                class="lg:absolute z-20 relative space-x-2 w-fit shadow-black/80 lg:right-4 lg:bottom-auto lg:left-auto lg:top-4 rounded-md"
            >
                <AppButton
                    class="shadow-2xl"
                    @click="
                        () => {
                            $emit('close');
                            $router.push('/account');
                        }
                    "
                >
                    {{ $t('auth.account') }}
                </AppButton>
                <AppButton isSecondary class="shadow-2xl" @click="logout"> {{ $t('auth.signOut') }}</AppButton>
            </div>

            <!--   <div
                class="lg:absolute relative z-20 lg:right-4 w-fit border-black/20 shadow-2xl mt-2 lg:mt-4 shadow-black/80 lg:top-12 rounded-md"
            >
                <ConnectButton class="w-full" />
            </div> -->
        </div>

        <div
            class="lg:absolute hidden lg:flex z-20 space-x-2 lg:right-4 lg:bottom-auto lg:left-auto lg:top-4"
            v-if="!$keycloak?.authenticated"
        >
            <!--  <div class="shadow-2xl shadow-black/80 rounded-md">
                <AppButton @click="signin">Sign in</AppButton>
            </div>

            <div class="shadow-2xl shadow-black/80 rounded-md">
                <AppButton isSecondary @click="signup">Sign up</AppButton>
            </div> -->
        </div>

        <div
            :class="fixed ? 'lg:fixed mt-2 lg:mt-5' : ' lg:absolute  mt-3'"
            class="hidden lg:block relative z-20 lg:right-4 w-fit border-black/20 shadow-black/80 lg:top-0 rounded-md"
        >
            <div class="md:flex items-center lg:space-x-1.5 space-y-2 md:space-y-0">
                <!--    <AppButton isSecondary class="shadow-2xl" @click="logout"> {{ $t('header.sign_out') }}</AppButton> -->

                <div class="flex">
                    <div
                        class="px-3 py-2 select-none flex rounded-l-md items-center w-full border-l border-t border-gray/20 font-semibold text-black bg-gradient-to-b from-white to-primary/10 border-b border-b-primary"
                    >
                        <FontAwesomeIcon class="w-4 h-4 mr-1.5" :icon="['far', 'user-circle']" />

                        {{ trimmedUsername }}
                    </div>
                    <div
                        class="rounded-r-md py-[0.45rem] px-3 border-l border-l-dark-200 flex justify-start border border-gray/20 items-center cursor-pointer text-black duration-300 transition-all ease hover:bg-gradient-to-b from-white to-primary/10 hover:border-b hover:border-b-primary"
                        @click="logout"
                    >
                        <FontAwesomeIcon class="w-4 h-4 mr-1.5" :icon="['fas', 'arrow-right-from-bracket']" />
                    </div>
                </div>

                <div class="flex duration-300 transition-all ease">
                    <AppLanguageSwitcher bold dapp />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
